import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Dialog, SearchBar, Space } from 'antd-mobile';
import { SearchOutline } from 'antd-mobile-icons';

// Import from new structure
import { ClientJson } from '../../../types/models';
import { useNavbar } from '../../../hooks/use-navbar';
import {
  useBlockedClients,
  findBlockedMatch,
  ensureBlockedData,
} from '../../../hooks/use-blocked-clients';
import { faroLogger } from '../../../observability/faro-logger';

import ClientList from './ClientList';

const formatEUR = (n: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(n);

const Home: FC = () => {
  const navigate = useNavigate();
  const navbar = useNavbar();
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const blocked = useBlockedClients();

  const onClickSearch = (): void => {
    setShowSearch((showSearch) => !showSearch);
  };

  useEffect(() => {
    if (navbar) {
      console.log('set Navbar');
      navbar.setTitle('Clients');
      navbar.setBackArrow(false);
      navbar.setRight(
        <div style={{ fontSize: 24 }}>
          <Space style={{ '--gap': '16px' }}>
            <SearchOutline onClick={onClickSearch} />
          </Space>
        </div>,
      );
    }
  }, []);

  const handleChoose = async (client: ClientJson): Promise<void> => {
    // On ne dépend plus de l'état local du hook : on lit (ou force le fetch)
    // depuis le cache module-scope pour éviter la fenêtre vide au remount.
    const list = blocked.enabled ? await ensureBlockedData() : [];
    const match = list.length > 0 ? findBlockedMatch(client.name, list) : null;

    faroLogger.info('blocked_check_picker', {
      clientId: client.id,
      clientName: client.name,
      enabled: blocked.enabled,
      listSize: list.length,
      matched: !!match,
      blockedName: match?.name,
    });

    if (!match) {
      navigate(`/client/${client.id}`);
      return;
    }

    faroLogger.warn('blocked_client_picker_warning_shown', {
      clientId: client.id,
      clientName: client.name,
      blockedName: match.name,
      totalDue: match.totalDue,
      invoices: match.invoices.length,
    });

    Dialog.confirm({
      cancelText: 'Annuler',
      confirmText: 'Continuer quand même',
      content: (
        <div style={{ textAlign: 'left' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#E63946',
              marginBottom: 6,
            }}
          >
            ⚠ Client bloqué
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
            {client.name}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginBottom: 10 }}>
            (correspond à <i>{match.name}</i> dans la liste comptable)
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            {match.invoices.length} facture
            {match.invoices.length > 1 ? 's' : ''} impayée
            {match.invoices.length > 1 ? 's' : ''} ·{' '}
            <b style={{ color: '#E63946' }}>{formatEUR(match.totalDue)}</b> dûs au total.
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'rgba(0,0,0,0.6)',
              marginTop: 12,
              lineHeight: 1.4,
            }}
          >
            Êtes-vous sûr·e de vouloir saisir une nouvelle intervention pour ce client ?
          </div>
        </div>
      ),
      onConfirm: () => {
        faroLogger.warn('blocked_client_picker_continued', {
          clientId: client.id,
          clientName: client.name,
          blockedName: match.name,
          totalDue: match.totalDue,
        });
        navigate(`/client/${client.id}`);
      },
    });
  };

  return (
    <>
      {showSearch && (
        <Card className="dark-background">
          <SearchBar placeholder="Client" onChange={(val) => setSearch(val)} onClear={() => setSearch('')} />
        </Card>
      )}
      <ClientList search={search} onChooseClient={handleChoose} />
    </>
  );
};

export default Home;
